import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  GraduationCap,
  Search,
  Lock,
  CheckCircle2,
  ExternalLink,
  Star,
  Clock,
  Award,
  Target,
  Sparkles,
  X,
  Briefcase,
  MapPin,
} from "lucide-react";

export default function Certificates() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [locked, setLocked] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [prov, setProv] = useState("");
  const [data, setData] = useState({ results: [], categories: [], providers: [] });
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [gateway, setGateway] = useState(() => localStorage.getItem("jf_gateway") || "stripe");

  // Certificate → Job match recommender state
  const [matchCourse, setMatchCourse] = useState(null); // the course being matched
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);

  const openMatch = async (course) => {
    setMatchCourse(course);
    setMatchData(null);
    setMatchLoading(true);
    try {
      const r = await api.post("/certificates/match-jobs", {
        course_title: course.title,
        course_author: course.author,
        category: course.category,
        provider: course.provider,
        limit: 6,
      });
      setMatchData(r.data);
    } catch (err) {
      toast.error("Could not compute job matches");
      setMatchCourse(null);
    } finally {
      setMatchLoading(false);
    }
  };

  const closeMatch = () => {
    setMatchCourse(null);
    setMatchData(null);
  };

  useEffect(() => {
    localStorage.setItem("jf_gateway", gateway);
  }, [gateway]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/certificates/search", {
        params: { q, category: cat, provider: prov },
      });
      setData(r.data);
      setAccess(r.data.access);
      setLocked(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        setLocked(true);
        setAccess({
          active: false,
          via: null,
          price_sar: err.response.data?.detail?.price_sar || 50,
        });
      } else {
        toast.error("Could not load courses");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // auto-confirm payment if returning from checkout
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const gw = params.get("gateway") || "stripe";
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`, { params: { gateway: gw } });
        if (r.data.processed && r.data.payment_status === "paid") {
          toast.success("Certificates access unlocked");
          window.history.replaceState({}, "", window.location.pathname);
          load();
          return;
        }
        if (["expired", "failed", "canceled"].includes(r.data.payment_status)) {
          toast.error(`Payment ${r.data.payment_status}`);
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (attempts < 10) setTimeout(poll, 2000);
      } catch {
        if (attempts < 10) setTimeout(poll, 2500);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = async () => {
    setPaying(true);
    try {
      const r = await api.post("/billing/checkout", {
        plan: "certs",
        gateway,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        // Return to /app/certificates with session_id
        const sep = r.data.url.includes("?") ? "&" : "?";
        window.location.href = r.data.url;
        return;
      }
      toast.error("Checkout unavailable");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Checkout failed");
    } finally {
      setPaying(false);
    }
  };

  const search = (e) => {
    e?.preventDefault();
    load();
  };

  if (locked) {
    return (
      <div className="space-y-8 max-w-4xl" data-testid="certificates-page">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
            / Certificates &amp; Courses
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            Upgrade your career
            <br />
            <span className="text-gradient">one certificate at a time.</span>
          </h1>
        </div>

        <div className="border border-black/10 bg-[#F8F9FA] p-8 md:p-10" data-testid="certs-paywall">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-6 h-6 text-[#FF3B30]" />
            <div className="font-mono text-xs uppercase tracking-widest text-[#FF3B30]">
              Access locked
            </div>
          </div>
          <div className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">
            SAR 50 / month
          </div>
          <p className="text-gray-700 max-w-xl mb-6">
            Unlock a live catalogue of 20+ flagship courses & certifications from Coursera, edX,
            Udemy, LinkedIn Learning, AWS, Google Cloud, Microsoft Learn, IBM SkillsBuild, Doroob
            and Misk. Manual monthly renewal — no auto-bill. Included free with Pro &amp; Agency.
          </p>

          <div
            className="border border-black/10 bg-white p-4 flex items-center gap-3 mb-6 flex-wrap"
            data-testid="certs-gateway-card"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Pay with
            </div>
            <div className="flex gap-0 border border-black/10 bg-[#F8F9FA]">
              <button
                onClick={() => setGateway("stripe")}
                className={`px-4 py-2 text-sm font-bold transition-colors ${
                  gateway === "stripe" ? "bg-black text-white" : "hover:bg-black/5"
                }`}
                data-testid="certs-gateway-stripe"
              >
                Stripe
              </button>
              <button
                onClick={() => setGateway("moyasar")}
                className={`px-4 py-2 text-sm font-bold transition-colors ${
                  gateway === "moyasar" ? "bg-[#0033FF] text-white" : "hover:bg-black/5"
                }`}
                data-testid="certs-gateway-moyasar"
              >
                Moyasar
              </button>
            </div>
          </div>

          <button
            onClick={unlock}
            disabled={paying}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-8 py-4 flex items-center gap-3 transition-colors disabled:opacity-60"
            data-testid="certs-unlock-btn"
          >
            <GraduationCap className="w-5 h-5" />
            {paying ? "Redirecting…" : "Unlock for SAR 50"}
          </button>
          <div className="mt-5 text-xs text-gray-500 font-mono">
            Tip: upgrade to <span className="font-bold text-black">Pro (SAR 360/mo)</span> or{" "}
            <span className="font-bold text-black">Agency</span> and Certificates is included free.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="certificates-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
          / Certificates &amp; Courses
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              Live course engine
              <br />
              <span className="text-gradient">for Saudi professionals.</span>
            </h1>
            <p className="text-gray-600 mt-3 max-w-2xl">
              Search accredited programs from Coursera, edX, Udemy, LinkedIn Learning, AWS, Google
              Cloud, Microsoft, IBM, Doroob and Misk — all curated for KSA career tracks.
            </p>
          </div>
          {access && (
            <div className="border border-[#34C759] bg-[#34C759]/10 px-4 py-3 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
              <div>
                <div className="font-bold text-[#34C759]">Access active</div>
                <div className="text-[11px] text-gray-600">
                  {access.via === "plan"
                    ? `Included in ${access.plan?.toUpperCase()} plan`
                    : access.expires_at
                    ? `Expires ${new Date(access.expires_at).toLocaleDateString()}`
                    : "Active"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search filters */}
      <form
        onSubmit={search}
        className="border border-black/10 bg-white p-5 grid md:grid-cols-[1fr_200px_200px_auto] gap-3 items-end"
        data-testid="certs-search-form"
      >
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Search
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="data, pmp, aws, cybersecurity…"
            className="w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-2"
            data-testid="certs-q"
          />
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Category
          </span>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="w-full bg-white border-b-2 border-gray-200 focus:border-black outline-none py-2"
            data-testid="certs-category"
          >
            <option value="">All</option>
            {data.categories?.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Provider
          </span>
          <select
            value={prov}
            onChange={(e) => setProv(e.target.value)}
            className="w-full bg-white border-b-2 border-gray-200 focus:border-black outline-none py-2"
            data-testid="certs-provider"
          >
            <option value="">All</option>
            {data.providers?.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={loading}
          className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-3 flex items-center gap-2 transition-colors disabled:opacity-60"
          data-testid="certs-search-btn"
        >
          <Search className="w-4 h-4" />
          {loading ? "…" : "Search"}
        </button>
      </form>

      {/* Results */}
      <div
        className="grid md:grid-cols-2 xl:grid-cols-3 gap-5"
        data-testid="certs-results"
      >
        {data.results?.map((c, i) => (
          <div
            key={i}
            className="border border-black/10 bg-white p-6 hover:border-black transition-colors group flex flex-col"
            data-testid={`certs-card-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF]">
                {c.provider}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <Star className="w-3 h-3 fill-[#FFD60A] text-[#FFD60A]" /> {c.rating}
              </div>
            </div>
            <h3 className="font-display font-bold text-lg tracking-tight mb-2 leading-snug">
              {c.title}
            </h3>
            <div className="text-xs text-gray-600 mb-4">by {c.author}</div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-[10px] font-mono uppercase tracking-widest border border-black/10 px-2 py-1">
                {c.category}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest border border-black/10 px-2 py-1">
                {c.level}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest border border-black/10 px-2 py-1 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {c.duration}
              </span>
            </div>
            <div className="mt-auto pt-3 border-t border-black/5 flex items-center justify-between gap-3">
              <span className="text-sm font-bold">{c.price}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openMatch(c)}
                  className="text-xs px-3 py-1.5 border border-[#0033FF] text-[#0033FF] hover:bg-[#0033FF] hover:text-white transition-colors flex items-center gap-1 font-bold"
                  data-testid={`certs-match-btn-${i}`}
                >
                  <Target className="w-3 h-3" /> Match jobs
                </button>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-1.5 border border-black bg-black text-white hover:bg-[#0033FF] hover:border-[#0033FF] transition-colors flex items-center gap-1 font-bold"
                  data-testid={`certs-enroll-${i}`}
                >
                  Enroll <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.results?.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-16">
          <Award className="w-10 h-10 mx-auto mb-3 stroke-[1]" />
          <p>No courses match your filters. Try broader keywords.</p>
        </div>
      )}

      {/* Certificate → Job Match Modal */}
      {matchCourse && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur flex items-center justify-center p-4"
          onClick={closeMatch}
          data-testid="certs-match-modal"
        >
          <div
            className="bg-white w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col border border-black/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-black/10 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-[#0033FF]" />
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0033FF]">
                    AI job match · Claude Sonnet
                  </div>
                </div>
                <h2 className="font-display font-black text-xl md:text-2xl tracking-tighter leading-tight">
                  Jobs unlocked by
                  <br />
                  <span className="text-gradient">{matchCourse.title}</span>
                </h2>
                <div className="text-xs text-gray-500 mt-1">
                  {matchCourse.provider} · {matchCourse.author}
                </div>
              </div>
              <button
                onClick={closeMatch}
                className="p-2 hover:bg-black/5"
                data-testid="certs-match-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {matchLoading ? (
                <div className="py-16 text-center">
                  <Sparkles className="w-8 h-8 text-[#0033FF] mx-auto mb-3 animate-pulse" />
                  <div className="font-mono text-xs uppercase tracking-widest text-gray-500">
                    Scoring Saudi jobs against your profile…
                  </div>
                </div>
              ) : matchData && matchData.matches?.length > 0 ? (
                <div className="space-y-4">
                  {matchData.profile_hint?.titles?.length === 0 && (
                    <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 text-xs text-gray-700 flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                      <span>
                        Tip: add target job titles &amp; cities to your profile for sharper matches.
                      </span>
                    </div>
                  )}
                  {matchData.matches.map((m, i) => (
                    <div
                      key={m.id}
                      className="border border-black/10 p-4 hover:border-black transition-colors"
                      data-testid={`certs-match-job-${i}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-base leading-tight">
                            {m.title}
                          </div>
                          <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" /> {m.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {m.city}
                            </span>
                            {m.salary_range && (
                              <span className="font-mono">{m.salary_range}</span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`font-display font-black text-2xl tracking-tighter flex-shrink-0 ${
                            m.score >= 80
                              ? "text-[#34C759]"
                              : m.score >= 60
                              ? "text-[#0033FF]"
                              : "text-gray-500"
                          }`}
                        >
                          {m.score}
                          <span className="text-[10px] font-mono font-normal align-top ml-0.5">
                            /100
                          </span>
                        </div>
                      </div>
                      {m.reason && (
                        <div className="text-sm text-gray-700 border-l-2 border-[#0033FF] pl-3 mt-2">
                          {m.reason}
                        </div>
                      )}
                      {m.source_url && (
                        <a
                          href={m.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#0033FF] hover:underline mt-3"
                        >
                          View posting <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center text-gray-500">
                  <Briefcase className="w-8 h-8 mx-auto mb-3 stroke-[1]" />
                  <p className="text-sm">
                    No strong matches found for this course yet. Try browsing the Search
                    Engine with related keywords.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-black/10 p-4 flex items-center justify-between gap-3 flex-wrap bg-[#F8F9FA]">
              <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
                {matchData?.matches?.length || 0} match
                {(matchData?.matches?.length || 0) === 1 ? "" : "es"} · ranked by Claude
              </div>
              <div className="flex gap-2">
                <a
                  href={matchCourse.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-4 py-2 border border-black bg-black text-white hover:bg-[#0033FF] hover:border-[#0033FF] flex items-center gap-1 font-bold"
                  data-testid="certs-match-enroll"
                >
                  Enroll in course <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={closeMatch}
                  className="text-xs px-4 py-2 border border-black/10 hover:bg-black/5 font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
